import {
  IFingerUnit,
  IGeneralWebsocketResponse,
  ISpecificWebsocketResponse,
  responseSocket,
} from "../store/models/FingerPrintModel";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  FingerPrintSelector,
  setLoadingFingerPrint,
  setResponseSocket,
} from "../store/slices/FingerPrintSlice";
import React, { useEffect } from "react";
import useFingerWebSocket from "./useFingerWebSocket";

interface Props {
  isClose: any;
  arrayFinger?: IFingerUnit[];
  handleAcceptButton?: Function;
  clearTimer?: Function;
  keyProp?: number;
  fingerId: any;
}

const useCaptueFingerprint = ({
  fingerId,
  keyProp,
  arrayFinger,
  handleAcceptButton,
  isClose,
  clearTimer,
}: Props) => {
  // local states
  const [capture, setCapture] =
    React.useState<ISpecificWebsocketResponse | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [nextFinger, setNextFinger] = React.useState<boolean>(true);
  // REDUX
  const dispatch = useDispatch();
  const { responseSocket, fingerDevice, loadingFingerPrint } =
    useSelector(FingerPrintSelector);
  // react hooks

  const {
    sendRequestSocket,
    disconnect,
    connectToFingerWebSocket,
    returnResponseSend,
    returnResponseConnect,
  } = useFingerWebSocket(fingerDevice);

  // custom functions
  // handle accept button on capture modal
  const onConfirmClick = () => {
    setCapture(null);
    setNextFinger(true);
    if (arrayFinger) {
      if (keyProp !== undefined) {
        const item = arrayFinger[keyProp + 1]
          ? arrayFinger[keyProp + 1].value
          : null;
        if (handleAcceptButton) {
          handleAcceptButton(item);
        }
      }
    }
    disconnect(); //try disconnect socket
  };

  // prepare device to capture mode
  const handleCaptureFinger = () => {
    if (fingerDevice) {
      dispatch(setLoadingFingerPrint(true));
      connectToFingerWebSocket(
        onResponseConnect,
        onResponseSocket,
        enqueueSnackbar
      );
    } else {
      enqueueSnackbar(
        "No se ha detectado el dispositivo biométrico. Por favor verifique la conexión del lector.",
        {
          variant: "error",
        }
      );
    }
  };

  // response device connection
  const onResponseConnect = (value: Boolean) => {
    if (value) {
      const connected = returnResponseConnect();
      if (connected === "CONNECTED") {
        if (fingerDevice) {
          sendRequestSocket(fingerDevice.result);
        }
      }
    }
  };

  // response by socket finger data
  const onResponseSocket = (value: Boolean) => {
    let response: IGeneralWebsocketResponse = returnResponseSend();
    if (value && response !== undefined) {
      if (!response.responseCode) {
        getResponseSocket(response);
      } else {
        enqueueSnackbar(response.message, {
          variant: "error",
        });
      }
    }
    disconnect();
    dispatch(setLoadingFingerPrint(false));
  };

  // comparisson finger data and validate user fingerprint
  const getResponseSocket = (response: IGeneralWebsocketResponse) => {
    if (response.responsePrintFinger !== undefined) {
      let captureFinger: ISpecificWebsocketResponse =
        response.responsePrintFinger;
      if (captureFinger.imageBase64 !== null) {
        setCapture(captureFinger);
        let verify = responseSocket.some((item) => item.id === fingerId);
        if (verify) {
          let index = responseSocket.findIndex((item) => item.id === fingerId);
          let element: responseSocket[] = [...responseSocket];
          element[index] = { ...element[index], response: captureFinger };
          dispatch(setResponseSocket(element));
        } else {
          dispatch(
            setResponseSocket([
              ...responseSocket,
              { id: fingerId, response: captureFinger },
            ])
          );
        }
        enqueueSnackbar("Captura de huella éxitosa", {
          variant: "success",
        });
        setNextFinger(false);
      } else {
        enqueueSnackbar("No se pudo capturar la huella", {
          variant: "error",
        });
        dispatch(setLoadingFingerPrint(false));
        setNextFinger(true);
      }
      disconnect(); //try disconnect socket after get response
    }
    dispatch(setLoadingFingerPrint(false));
  };

  // cancel capture modal and reset variables
  const handleCancel = () => {
    isClose();
    setCapture(null);
    setNextFinger(true);
    disconnect();
  };

  useEffect(() => {
    if (!fingerId) setCapture(null);
    return () => {
      !!clearTimer && clearTimer();
      dispatch(setLoadingFingerPrint(false));
    };
  }, [fingerId]);

  return {
    onConfirmClick,
    handleCancel,
    handleCaptureFinger,
    capture,
    nextFinger,
    loadingFingerPrint,
  };
};

export default useCaptueFingerprint;
